<template>
    <div class="RiskProfileAssignmentContent">
        <RiskProfileAssignmentInstruction
            v-if="!assignmentRule"
            :title="emptyAssignmentText.title"
            :description="emptyAssignmentText.description"
        />
        <RiskProfileAssignmentInstruction
            v-else-if="assignmentRule.type === AssignmentRuleType.all"
            :title="allEntitiesAssignmentText.title"
            :description="allEntitiesAssignmentText.description"
        />
        <EntitySearchSelection
            v-else-if="assignmentRule.type === AssignmentRuleType.single"
            :instruction="singleEntityAssignmentInstruction"
            :selected-entity-id="selectedEntityId"
            :is-disabled="isDisabled"
            @select="onEntitySelect"
        />
        <RiskProfileAssignmentRuleBased
            v-else-if="assignmentRule.type === AssignmentRuleType.selected"
            :rules="basicAssignmentRules"
            :custom-properties="customProperties"
            :loading-custom-properties="isLoadingCustomProperties"
            :collaterals-only="isCollateralProfile"
            :get-requirement-type-label="getRequirementTypeLabel"
            :requirement-types="requirementTypes"
            :is-disabled="isDisabled"
            @update-rules="updateSelectedRules"
        />
    </div>
</template>

<style lang="scss">
.RiskProfileAssignmentContent {
    .EntitySearchSelection {
        height: 500px;
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { AssignmentRuleType, AssignmentRule } from '@evidentid/tprm-portal-lib/models/decisioning/AssignmentRule.model';
    import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';

    import EntitySearchSelection from '@/components/entity-search-selection/EntitySearchSelection.vue';
    import { GetRequirementTypeLabel } from '@/modules/decisioning-criteria/components/CreateEditRiskProfile/types';
    import { BasicAssignmentRule } from '@/modules/decisioning-criteria/types';
    import { convertToBasicAssignmentRules, encodeAssignmentSelectedRules } from '@/modules/decisioning-criteria/utils/parseRulesLogic';

    import RiskProfileAssignmentInstruction
        from './RiskProfileAssignmentInstruction.vue';
    import RiskProfileAssignmentRuleBased
        from './RiskProfileAssignmentRuleBased.vue';

    export default defineComponent({
        name: 'RiskProfileAssignmentContent',
        components: {
            RiskProfileAssignmentInstruction,
            RiskProfileAssignmentRuleBased,
            EntitySearchSelection,
        },
        props: {
            customProperties: {
                type: Array as PropType<CustomProperty[]>,
                default: (): CustomProperty[] => [],
            },
            isLoadingCustomProperties: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            assignmentRule: {
                type: Object as PropType<AssignmentRule | null>,
                default: null,
            },
            requirementTypes: {
                type: Array as PropType<TprmRequirementType[]>,
                default: (): TprmRequirementType[] => [],
            },
            getRequirementTypeLabel: {
                type: Function as PropType<GetRequirementTypeLabel>,
                required: true,
            },
            isCollateralProfile: {
                type: Boolean as PropType<Boolean>,
                default: false,
            },
            isDisabled: {
                type: Boolean,
                default: false,
            },
        },
        emits: [ 'update-rules' ],
        data() {
            return {
                AssignmentRuleType,
                emptyAssignmentText: {
                    title: 'Configure Assignment Rules',
                    description: 'Choose how this risk profile should be assigned to entities in your organization. Select "All Entities" to apply to everyone, or "Select Entities" to create custom rules based on entity properties.',
                },
                allEntitiesAssignmentText: {
                    title: 'Applied to all entities',
                    description: 'This risk profile will be automatically assigned to every entity in your organization.',
                },
                singleEntityAssignmentInstruction: 'This profile will be assigned to one entity only. To assign to multiple entities based on conditions, use \'Rule-based entities\'.',
            };
        },
        computed: {
            basicAssignmentRules(): BasicAssignmentRule[][] {
                if (this.assignmentRule?.type === AssignmentRuleType.selected &&
                    this.assignmentRule?.expression
                ) {
                    return convertToBasicAssignmentRules(this.assignmentRule.expression);
                }
                return [];
            },
            selectedEntityId(): string | null {
                if (this.assignmentRule?.type === AssignmentRuleType.single) {
                    return this.assignmentRule.entityId || null;
                }
                return null;
            },
        },
        methods: {
            onEntitySelect(entityId: string | null): void {
                this.$emit('update-rules', {
                    type: AssignmentRuleType.single,
                    entityId,
                });
            },
            updateSelectedRules(rules: BasicAssignmentRule[][]): void {
                this.$emit('update-rules', {
                    type: AssignmentRuleType.selected,
                    expression: encodeAssignmentSelectedRules(rules),
                });
            },
        },
    });
</script>
